import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Trash2, Save, Plus, Activity } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SLOTargetsForm — per-workload PromQL query + threshold overrides.
 *
 * Lists slo_targets for the cluster, lets the operator add or update one,
 * and persists via the slo_routes CRUD endpoints. The agent's
 * BackendTargetSource picks up changes within ~5 minutes (its refresh
 * interval) so PromQL edits during the demo show their effect on the
 * very next scrape.
 *
 * Live in cluster settings as a sibling card to RightsizingSettings.
 */
export default function SLOTargetsForm({ clusterId }) {
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draft, setDraft] = useState(emptyDraft());

    const refresh = useCallback(async () => {
        if (!clusterId) return;
        setLoading(true);
        try {
            const resp = await axios.get(`${API}/clusters/${clusterId}/slo-targets`);
            setTargets(resp.data?.targets || []);
        } catch (e) {
            console.error('SLO targets list failed:', e);
        } finally {
            setLoading(false);
        }
    }, [clusterId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleSave = async () => {
        if (!draft.namespace || !draft.workload_name) {
            toast.error('Namespace and workload name are required.');
            return;
        }
        const path = `${draft.namespace}/${draft.workload_name}`;
        const queries = {};
        for (const k of QUERY_KEYS) {
            if (draft.queries[k]) queries[k] = draft.queries[k];
        }
        const thresholds = {};
        if (draft.thresholds.latency_p95_regression_pct !== '') {
            thresholds.latency_p95_regression_pct = Number(draft.thresholds.latency_p95_regression_pct);
        }
        if (draft.thresholds.error_rate_absolute_increase !== '') {
            thresholds.error_rate_absolute_increase = Number(draft.thresholds.error_rate_absolute_increase);
        }
        try {
            await axios.put(
                `${API}/clusters/${clusterId}/slo-targets/${path}`,
                { queries, thresholds },
            );
            toast.success(`Saved SLO target for ${path}`);
            setDraft(emptyDraft());
            refresh();
        } catch (e) {
            toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    const handleEdit = (t) => {
        setDraft({
            namespace: t.namespace,
            workload_name: t.workload_name,
            queries: {
                latency_p95_ms: t.queries?.latency_p95_ms || '',
                error_rate: t.queries?.error_rate || '',
                throughput_rps: t.queries?.throughput_rps || '',
            },
            thresholds: {
                latency_p95_regression_pct: t.thresholds?.latency_p95_regression_pct ?? '',
                error_rate_absolute_increase: t.thresholds?.error_rate_absolute_increase ?? '',
            },
        });
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Delete SLO target for ${t.namespace}/${t.workload_name}?`)) return;
        const path = `${t.namespace}/${t.workload_name}`;
        try {
            await axios.delete(`${API}/clusters/${clusterId}/slo-targets/${path}`);
            toast.success(`Deleted ${path}`);
            refresh();
        } catch (e) {
            toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    return (
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-lg">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-indigo-600" />
                    SLO Targets — per-workload golden-signal queries
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                    Override the default Prometheus queries the agent uses to scrape latency,
                    error rate, and throughput per workload. Agent picks up changes within ~5 min.
                </p>
            </CardHeader>
            <CardContent className="space-y-6">
                <ExistingTargets
                    targets={targets} loading={loading}
                    onEdit={handleEdit} onDelete={handleDelete}
                />
                <DraftEditor draft={draft} setDraft={setDraft} onSave={handleSave} />
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------------------

const QUERY_KEYS = ['latency_p95_ms', 'error_rate', 'throughput_rps'];

function emptyDraft() {
    return {
        namespace: '',
        workload_name: '',
        queries: { latency_p95_ms: '', error_rate: '', throughput_rps: '' },
        thresholds: { latency_p95_regression_pct: '', error_rate_absolute_increase: '' },
    };
}

function ExistingTargets({ targets, loading, onEdit, onDelete }) {
    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!targets.length) {
        return (
            <div className="text-xs text-slate-500 italic">
                No SLO targets configured. Workloads without an override use the agent's default queries.
            </div>
        );
    }
    return (
        <table className="w-full text-xs border border-slate-200 rounded">
            <thead className="bg-slate-50">
                <tr>
                    <th className="text-left px-2 py-1 font-medium text-slate-600">Workload</th>
                    <th className="text-left px-2 py-1 font-medium text-slate-600">Configured queries</th>
                    <th className="text-left px-2 py-1 font-medium text-slate-600">Thresholds</th>
                    <th className="text-right px-2 py-1 font-medium text-slate-600">Actions</th>
                </tr>
            </thead>
            <tbody>
                {targets.map((t) => (
                    <tr key={`${t.namespace}/${t.workload_name}`} className="border-t border-slate-200">
                        <td className="px-2 py-2 font-mono text-[11px]">
                            {t.namespace}/{t.workload_name}
                        </td>
                        <td className="px-2 py-2">
                            {QUERY_KEYS.filter((k) => t.queries?.[k]).join(', ') || (
                                <span className="text-slate-400 italic">defaults</span>
                            )}
                        </td>
                        <td className="px-2 py-2">
                            {(t.thresholds?.latency_p95_regression_pct != null
                                ? `lat:+${t.thresholds.latency_p95_regression_pct}% `
                                : '')}
                            {(t.thresholds?.error_rate_absolute_increase != null
                                ? `err:+${t.thresholds.error_rate_absolute_increase}`
                                : '')}
                        </td>
                        <td className="px-2 py-2 text-right">
                            <Button
                                variant="ghost" size="sm"
                                className="text-indigo-600 hover:text-indigo-800 mr-1"
                                onClick={() => onEdit(t)}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="ghost" size="sm"
                                className="text-red-600 hover:text-red-800"
                                onClick={() => onDelete(t)}
                            >
                                <Trash2 className="w-3 h-3" />
                            </Button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DraftEditor({ draft, setDraft, onSave }) {
    const setQuery = (k, v) =>
        setDraft({ ...draft, queries: { ...draft.queries, [k]: v } });
    const setThreshold = (k, v) =>
        setDraft({ ...draft, thresholds: { ...draft.thresholds, [k]: v } });

    return (
        <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add or update target
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Namespace</Label>
                    <Input
                        value={draft.namespace}
                        onChange={(e) => setDraft({ ...draft, namespace: e.target.value })}
                        placeholder="shop"
                    />
                </div>
                <div>
                    <Label className="text-xs">Workload name</Label>
                    <Input
                        value={draft.workload_name}
                        onChange={(e) => setDraft({ ...draft, workload_name: e.target.value })}
                        placeholder="payment-service"
                    />
                </div>
            </div>
            {QUERY_KEYS.map((k) => (
                <div key={k}>
                    <Label className="text-xs font-mono">{k}</Label>
                    <Input
                        value={draft.queries[k]}
                        onChange={(e) => setQuery(k, e.target.value)}
                        placeholder={DEFAULT_QUERIES[k]}
                        className="font-mono text-[11px]"
                    />
                </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Latency regression threshold (%)</Label>
                    <Input
                        type="number" step="1"
                        value={draft.thresholds.latency_p95_regression_pct}
                        onChange={(e) => setThreshold('latency_p95_regression_pct', e.target.value)}
                        placeholder="10"
                    />
                </div>
                <div>
                    <Label className="text-xs">Error rate absolute increase</Label>
                    <Input
                        type="number" step="0.001"
                        value={draft.thresholds.error_rate_absolute_increase}
                        onChange={(e) => setThreshold('error_rate_absolute_increase', e.target.value)}
                        placeholder="0.005"
                    />
                </div>
            </div>
            <Button onClick={onSave} className="w-full flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> Save target
            </Button>
        </div>
    );
}

// Hint text shown as placeholders in the form — the actual defaults
// the agent applies are also defined here. The agent fetches per-workload
// queries from /v1/agents/slo-targets; if none configured, it falls back
// to these (Day 10 polish will register the defaults with the agent too).
const DEFAULT_QUERIES = {
    latency_p95_ms:
        'histogram_quantile(0.95, sum by (le) (rate(http_request_duration_seconds_bucket{namespace=\"$NS\",workload=\"$W\"}[5m]))) * 1000',
    error_rate:
        'sum(rate(http_requests_total{namespace=\"$NS\",workload=\"$W\",status=~\"5..\"}[5m])) / sum(rate(http_requests_total{namespace=\"$NS\",workload=\"$W\"}[5m]))',
    throughput_rps:
        'sum(rate(http_requests_total{namespace=\"$NS\",workload=\"$W\"}[5m]))',
};
